// packages/web/src/pages/MainPage.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Box, 
  CircularProgress,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { 
  startOfWeek, 
  addDays, 
  isWithinInterval,
  addWeeks
} from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { 
  Filters, 
  PlayerSlot, 
  Challenge, 
  ChallengeService, 
  PlayerSlotService
} from '@matchpoint/shared';

// Layout components
import { Header } from '../components/Layout/Header';
import { WeekNavigation } from '../components/Layout/WeekNavigation';
import { FilterPanel } from '../components/Layout/FilterPanel';
import { Footer } from '../components/Layout/Footer';
import { LoginDialog } from '../components/Auth/LoginDialog';
import { useBannerState } from '../hooks/useBannerState';

// Feature components
import { PublishScheduleBanner } from '../components/Schedule/PublishScheduleBanner';
import { UpcomingMatchesGallery } from '../components/Matches/UpcomingMatchesGallery';
import { PlayerSlotsList } from '../components/Slots/PlayerSlotsList';

// Timezone utilities
import { getBrowserTimezone, findClosestTimezone, formatTimezoneForDisplay } from '../components/Common/TimezoneSelector';

// Hooks
import { useAuth } from '../hooks/useAuth';
import { useGeohashHeatmap } from '../hooks/useGeohashHeatmap';

// Styled components - Updated to match LandingPage design
const MainContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  backgroundColor: '#ffffff', // Changed from #f8f9fa to white
});

const LocationBanner = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.75, 0),
  backgroundColor: '#fafafa', // Lighter background
  borderBottom: '1px solid #e5e5e5', // Updated border color
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
}));

const LocationContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  fontSize: '0.75rem', // Slightly larger
  color: '#666', // Updated text color to match LandingPage
  fontWeight: 400,
  maxWidth: '1200px',
  width: '100%',
  padding: theme.spacing(0, 2),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0, 1),
    fontSize: '0.7rem',
  },
}));

const MainContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  maxWidth: '1200px',
  margin: '0 auto',
  width: '100%',
  padding: theme.spacing(2, 2), // Increased padding
  gap: theme.spacing(2), // Increased gap
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5, 1),
    gap: theme.spacing(1.5),
  },
}));

interface ExtendedFilters extends Filters {
  radius?: number;
}

// Feature flag for IP-based geolocation (ipapi.co)
// Set to false if you want to disable IP geolocation (e.g., if free tier limit exhausted)
const ENABLE_IP_GEOLOCATION = true;

const MainPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, signIn, signUp, signOut } = useAuth();
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [currentWeek, setCurrentWeek] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  
  // Timezone state - controllable by user and persisted in localStorage
  const [selectedTimezone, setSelectedTimezone] = useState(() => {
    // First check localStorage
    const savedTimezone = localStorage.getItem('selectedTimezone');
    if (savedTimezone) {
      return savedTimezone;
    }
    
    // Otherwise use browser detection
    const browserTz = getBrowserTimezone();
    return findClosestTimezone(browserTz);
  });
  
  const inferLocationFromTimezone = (timezone: string): { latitude: number; longitude: number } | null => {
    const timezoneLocations: Record<string, { latitude: number; longitude: number }> = {
      'America/Los_Angeles': { latitude: 34.0522, longitude: -118.2437 },
      'America/New_York': { latitude: 40.7128, longitude: -74.0060 },
      'America/Chicago': { latitude: 41.8781, longitude: -87.6298 },
      'America/Denver': { latitude: 39.7392, longitude: -104.9903 },
      'America/Phoenix': { latitude: 33.4484, longitude: -112.0740 },
      'America/Toronto': { latitude: 43.6532, longitude: -79.3832 },
      'America/Vancouver': { latitude: 49.2827, longitude: -123.1207 },
      'UTC': { latitude: 51.5074, longitude: -0.1278 },
      'Europe/London': { latitude: 51.5074, longitude: -0.1278 },
      'Europe/Paris': { latitude: 48.8566, longitude: 2.3522 },
      'Europe/Berlin': { latitude: 52.5200, longitude: 13.4050 },
      'Europe/Madrid': { latitude: 40.4168, longitude: -3.7038 },
      'Europe/Rome': { latitude: 41.9028, longitude: 12.4964 },
      'Europe/Moscow': { latitude: 55.7558, longitude: 37.6173 },
      'Asia/Tokyo': { latitude: 35.6762, longitude: 139.6503 },
      'Asia/Shanghai': { latitude: 31.2304, longitude: 121.4737 },
      'Asia/Hong_Kong': { latitude: 22.3193, longitude: 114.1694 },
      'Asia/Singapore': { latitude: 1.3521, longitude: 103.8198 },
      'Asia/Dubai': { latitude: 25.2048, longitude: 55.2708 },
      'Asia/Mumbai': { latitude: 19.0760, longitude: 72.8777 },
      'Australia/Sydney': { latitude: -33.8688, longitude: 151.2093 },
      'Australia/Melbourne': { latitude: -37.8136, longitude: 144.9631 },
      'Australia/Perth': { latitude: -31.9505, longitude: 115.8605 },
      'Pacific/Auckland': { latitude: -36.8485, longitude: 174.7633 },
    };

    if (timezoneLocations[timezone]) {
      return timezoneLocations[timezone];
    }

    const lowerTz = timezone.toLowerCase();
    
    if (lowerTz.includes('los_angeles') || lowerTz.includes('pacific')) {
      return timezoneLocations['America/Los_Angeles'];
    }
    if (lowerTz.includes('new_york') || lowerTz.includes('eastern')) {
      return timezoneLocations['America/New_York'];
    }
    if (lowerTz.includes('chicago') || lowerTz.includes('central')) {
      return timezoneLocations['America/Chicago'];
    }
    if (lowerTz.includes('london')) {
      return timezoneLocations['Europe/London'];
    }
    if (lowerTz.includes('paris')) {
      return timezoneLocations['Europe/Paris'];
    }
    if (lowerTz.includes('tokyo')) {
      return timezoneLocations['Asia/Tokyo'];
    }
    if (lowerTz.includes('sydney')) {
      return timezoneLocations['Australia/Sydney'];
    }
    
    return null;
  };
  
  // Initialize location IMMEDIATELY from timezone - before any state declarations
  const initialLocation = inferLocationFromTimezone(selectedTimezone);
  
  const [userLocation, setUserLocation] = useState<{latitude: number, longitude: number} | null>(initialLocation);
  const [locationSource, setLocationSource] = useState<'browser' | 'inferred' | 'manual' | null>(initialLocation ? 'inferred' : null);
  const [manualLocation, setManualLocation] = useState<{latitude: number, longitude: number} | null>(null);
  
  // States for matches and slots
  const [upcomingMatches, setUpcomingMatches] = useState<Challenge[]>([]);
  const [playerSlots, setPlayerSlots] = useState<PlayerSlot[]>([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = useState(0);
  
  // Pagination state - use ref to avoid dependency issues
  const [hasMoreSlots, setHasMoreSlots] = useState(false);
  const lastDocSnapshotRef = useRef<any>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Separate state for current filters (being configured) and applied filters (used for data fetching)
  const [currentFilters, setCurrentFilters] = useState<ExtendedFilters>({
    level: 'any',
    utrMin: null,
    utrMax: null,
    ntrpMin: null,
    ntrpMax: null,
    court: 'any',
    duration: 'any',
    city: '',
    country: 'any',
    timezone: selectedTimezone,
    radius: 10,
  });

  const { 
    shouldShow: shouldShowBanner, 
    isLoading: bannerLoading, 
    checkSlots, 
    onSlotCreated, 
    onClose: onCloseBanner 
  } = useBannerState(user?.uid);

  const [appliedFilters, setAppliedFilters] = useState<ExtendedFilters>({
    ...currentFilters,
  });

  // Calculate effective location based on priority
  const effectiveLocation = manualLocation || userLocation;

  const handlePreviousWeek = () => {
    setCurrentWeek(prev => addWeeks(prev, -1));
  };

  const handleNextWeek = () => {
    setCurrentWeek(prev => addWeeks(prev, 1));
  };

  const handleJumpToDate = (date: Date) => {
    setCurrentWeek(startOfWeek(date, { weekStartsOn: 1 }));
  };

  const handleTimezoneChange = (newTimezone: string) => {
    setSelectedTimezone(newTimezone);
    
    // Save to localStorage
    localStorage.setItem('selectedTimezone', newTimezone);
    
    // Update current and applied filters
    setCurrentFilters(prev => ({
      ...prev,
      timezone: newTimezone,
    }));
    
    setAppliedFilters(prev => ({
      ...prev,
      timezone: newTimezone,
    }));
    
    // Infer location if no manual location is set
    if (!manualLocation) {
      const inferredLoc = inferLocationFromTimezone(newTimezone);
      if (inferredLoc) {
        setUserLocation(inferredLoc);
        setLocationSource('inferred');
      }
    }
  };

  // Load upcoming matches (challenges)
  const loadUpcomingMatches = useCallback(async () => {
    if (!user) {
      setUpcomingMatches([]);
      return;
    }

    setLoadingMatches(true);
    setError(null);

    try {
      const challengeService = ChallengeService.getInstance();
      await challengeService['firebaseService'].initializeApp();
      
      // Get both sent and received challenges
      const [sentChallenges, receivedChallenges] = await Promise.all([
        challengeService.getSentChallenges(user.uid),
        challengeService.getReceivedChallenges(user.uid)
      ]);

      // Filter for accepted and pending challenges in current week
      const weekStart = currentWeek;
      const weekEnd = addDays(currentWeek, 7);
      
      const relevantChallenges = [...sentChallenges, ...receivedChallenges]
        .filter(challenge => {
          if (challenge.status !== 'accepted' && challenge.status !== 'pending') return false;
          
          // Check if challenge is within current week
          const challengeDate = challenge.startTime;
          return isWithinInterval(challengeDate, { start: weekStart, end: weekEnd });
        })
        .sort((a, b) => a.startTime.getTime() - b.startTime.getTime());

      setUpcomingMatches(relevantChallenges);
    } catch (err) {
      console.error('Error loading matches:', err);
      setError('Failed to load upcoming matches');
    } finally {
      setLoadingMatches(false);
    }
  }, [user, currentWeek]);

  // Load player slots with filters
  const loadPlayerSlots = useCallback(async (loadMore: boolean = false) => {
    if (!appliedFilters) return;
    
    setError(null);
    
    if (!loadMore) {
      setLoadingSlots(true);
      lastDocSnapshotRef.current = null;
    } else {
      setLoadingMore(true);
    }
    
    try {
      const playerSlotService = PlayerSlotService.getInstance();
      await playerSlotService['firebaseService'].initializeApp();
      
      const endDate = addDays(currentWeek, 7);

      const result = await (playerSlotService as any).getFilteredPlayerSlotsPaginated(
        currentWeek,
        endDate,
        appliedFilters,
        20,
        loadMore ? lastDocSnapshotRef.current : null,
        effectiveLocation
      );
      
      const availableSlots = await playerSlotService.filterAvailableSlots(result.slots);
      
      if (loadMore) {
        setPlayerSlots(prev => [...prev, ...availableSlots]);
      } else {
        setPlayerSlots(availableSlots);
      }
      
      setHasMoreSlots(result.hasMore);
      lastDocSnapshotRef.current = result.lastDoc;
      
    } catch (err) {
      console.error('[MainPage] Error loading slots:', err);
      setError('Failed to load player slots. Please try again.');
    } finally {
      setLoadingSlots(false);
      setLoadingMore(false);
    }
  }, [appliedFilters, currentWeek, effectiveLocation]);

  const handleLoadMore = useCallback(() => {
    if (!loadingMore && hasMoreSlots) {
      loadPlayerSlots(true);
    }
  }, [loadPlayerSlots, loadingMore, hasMoreSlots]);

  // Request browser geolocation on mount
  useEffect(() => {
    // Only attempt browser geolocation if we don't already have it
    if (locationSource === 'inferred' || locationSource === null) {
      if (navigator.geolocation) {
        console.log('Attempting to get user location via browser...');
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const location = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude
            };
            console.log('User location detected via browser geolocation:', location);
            setUserLocation(location);
            setLocationSource('browser');
            // Note: Browser geolocation keeps the existing timezone (which was detected from browser)
            // since getBrowserTimezone() already gave us the correct timezone at initialization
          },
          async (error) => {
            console.warn('Browser geolocation failed:', error.message);
            
            // Second fallback: Try IP-based geolocation (if enabled)
            if (ENABLE_IP_GEOLOCATION) {
              try {
                console.log('Attempting IP-based geolocation via ipapi.co...');
                const response = await fetch('https://ipapi.co/json/', {
                  method: 'GET',
                  headers: {
                    'Accept': 'application/json',
                  },
                });
                
                if (response.ok) {
                  const data = await response.json();
                  
                  if (data.latitude && data.longitude && data.timezone) {
                    const location = {
                      latitude: data.latitude,
                      longitude: data.longitude
                    };
                    console.log('User location detected via IP geolocation:', location, `(${data.city}, ${data.region}, ${data.country_name})`);
                    console.log('Detected timezone from IP:', data.timezone);
                    
                    // Update timezone to match the detected location
                    const detectedTimezone = findClosestTimezone(data.timezone);
                    
                    // Batch all state updates together to avoid multiple re-renders
                    setUserLocation(location);
                    setLocationSource('browser');
                    setSelectedTimezone(detectedTimezone);
                    localStorage.setItem('selectedTimezone', detectedTimezone);
                    
                    console.log('Timezone updated to:', detectedTimezone);
                    return;
                  }
                }
                
                console.warn('IP geolocation failed or returned invalid data');
              } catch (ipError) {
                console.warn('IP geolocation error:', ipError);
              }
            } else {
              console.log('IP geolocation disabled via feature flag');
            }
            
            // Third fallback: Infer location from timezone
            const browserTz = getBrowserTimezone();
            const inferredLocation = inferLocationFromTimezone(browserTz);
            
            if (inferredLocation) {
              console.log('Using inferred location from timezone as final fallback');
              setUserLocation(inferredLocation);
              setLocationSource('inferred');
            } else {
              console.log('Could not determine user location - will use no location filter');
            }
          },
          {
            timeout: 10000, // 10 second timeout
            enableHighAccuracy: false // Don't require high accuracy
          }
        );
      } else {
        console.warn('Geolocation not supported by browser');
        
        // Browser geolocation not available, try IP-based geolocation as fallback (if enabled)
        const tryIPGeolocation = async () => {
          if (ENABLE_IP_GEOLOCATION) {
            try {
              console.log('Attempting IP-based geolocation via ipapi.co...');
              const response = await fetch('https://ipapi.co/json/', {
                method: 'GET',
                headers: {
                  'Accept': 'application/json',
                },
              });
              
              if (response.ok) {
                const data = await response.json();
                
                if (data.latitude && data.longitude && data.timezone) {
                  const location = {
                    latitude: data.latitude,
                    longitude: data.longitude
                  };
                  console.log('User location detected via IP geolocation:', location, `(${data.city}, ${data.region}, ${data.country_name})`);
                  console.log('Detected timezone from IP:', data.timezone);
                  
                  // Update timezone to match the detected location
                  const detectedTimezone = findClosestTimezone(data.timezone);
                  
                  // Batch all state updates together to avoid multiple re-renders
                  setUserLocation(location);
                  setLocationSource('browser');
                  setSelectedTimezone(detectedTimezone);
                  localStorage.setItem('selectedTimezone', detectedTimezone);
                  
                  console.log('Timezone updated to:', detectedTimezone);
                  return;
                }
              }
              
              console.warn('IP geolocation failed or returned invalid data');
            } catch (ipError) {
              console.warn('IP geolocation error:', ipError);
            }
          } else {
            console.log('IP geolocation disabled via feature flag');
          }
          
          // Final fallback: timezone-based inference
          const browserTz = getBrowserTimezone();
          const inferredLocation = inferLocationFromTimezone(browserTz);
          
          if (inferredLocation) {
            console.log('Using inferred location from timezone as final fallback');
            setUserLocation(inferredLocation);
            setLocationSource('inferred');
          }
        };
        
        tryIPGeolocation();
      }
    }
  }, []); // Run only once on mount

  // Sync filters when timezone changes (from geolocation detection)
  useEffect(() => {
    setCurrentFilters(prev => ({
      ...prev,
      timezone: selectedTimezone,
    }));
    setAppliedFilters(prev => ({
      ...prev,
      timezone: selectedTimezone,
    }));
  }, [selectedTimezone]);

  useEffect(() => {
    loadUpcomingMatches();
  }, [loadUpcomingMatches]);

  useEffect(() => {
    loadPlayerSlots();
  }, [loadPlayerSlots]);

  useEffect(() => {
    const checkUserSlots = async () => {
      if (!user) return;
      
      try {
        const playerSlotService = PlayerSlotService.getInstance();
        await playerSlotService['firebaseService'].initializeApp();
        
        const userSlots = await playerSlotService.getUserSlots(user.uid);
        checkSlots(userSlots.length > 0);
      } catch (err) {
        console.error('Error checking user slots:', err);
      }
    };

    if (user && !bannerLoading) {
      checkUserSlots();
    }
  }, [user, checkSlots, bannerLoading]);

  const handleChallenge = async (slot: PlayerSlot, message: string): Promise<void> => {
    if (!user) {
      setLoginDialogOpen(true);
      throw new Error('Please sign in to challenge players');
    }
    
    try {
      const challengeService = ChallengeService.getInstance();
      await challengeService['firebaseService'].initializeApp();
      
      await challengeService.createChallenge(
        user,
        slot.playerId,
        slot.playerName,
        slot,
        message
      );
      
      // Refresh data
      await Promise.all([loadUpcomingMatches(), loadPlayerSlots()]);
      setRefreshCounter(prev => prev + 1);
    } catch (err) {
      console.error('Challenge error:', err);
      throw err;
    }
  };

  const handleSlotsUpdate = useCallback(() => {
    setRefreshCounter(prev => prev + 1);
    loadPlayerSlots();
    loadUpcomingMatches();
  }, [loadPlayerSlots, loadUpcomingMatches]);

  const handlePublishSchedule = () => {
    if (!user) {
      setLoginDialogOpen(true);
      return;
    }
    navigate('/profile');
  };

  // Handle match action buttons
  const handleAcceptMatch = async (challengeId: string) => {
    try {
      const challengeService = ChallengeService.getInstance();
      await challengeService.acceptChallenge(challengeId);
      await loadUpcomingMatches();
    } catch (err) {
      console.error('Accept error:', err);
    }
  };

  const handleDeclineMatch = async (challengeId: string) => {
    try {
      const challengeService = ChallengeService.getInstance();
      await challengeService.rejectChallenge(challengeId);
      await loadUpcomingMatches();
    } catch (err) {
      console.error('Decline error:', err);
    }
  };

  const handleViewDetails = (challenge: Challenge) => {
    console.log('🎯 View Details clicked in MainPage');
    console.log('Challenge:', {
      id: challenge.id,
      fromUserId: challenge.fromUserId,
      toUserId: challenge.toUserId,
      currentUserId: user?.uid
    });

    if (!user?.uid) {
      console.error('❌ No user ID available');
      return;
    }

    // Determine if this challenge was sent or received
    const isReceived = challenge.toUserId === user.uid;
    
    console.log('Is received:', isReceived);
    
    // Navigate to the appropriate tab
    const targetTab = isReceived ? 'received' : 'sent';
    
    console.log(`📍 Navigating to /profile?tab=${targetTab}`);
    navigate(`/profile?tab=${targetTab}`);
  };

  const handleManageSchedule = () => {
    console.log('📅 Manage schedule clicked');
    navigate('/profile?tab=schedule');
  };

  // Helper function to geocode city to coordinates
  const geocodeCity = async (city: string, country?: string): Promise<{ latitude: number; longitude: number } | null> => {
    try {
      const cityCoordinates: Record<string, { latitude: number; longitude: number }> = {
        // US Cities
        'new york': { latitude: 40.7128, longitude: -74.0060 },
        'los angeles': { latitude: 34.0522, longitude: -118.2437 },
        'chicago': { latitude: 41.8781, longitude: -87.6298 },
        'houston': { latitude: 29.7604, longitude: -95.3698 },
        'phoenix': { latitude: 33.4484, longitude: -112.0740 },
        'philadelphia': { latitude: 39.9526, longitude: -75.1652 },
        'san antonio': { latitude: 29.4241, longitude: -98.4936 },
        'san diego': { latitude: 32.7157, longitude: -117.1611 },
        'dallas': { latitude: 32.7767, longitude: -96.7970 },
        'san jose': { latitude: 37.3382, longitude: -121.8863 },
        'austin': { latitude: 30.2672, longitude: -97.7431 },
        'seattle': { latitude: 47.6062, longitude: -122.3321 },
        'san francisco': { latitude: 37.7749, longitude: -122.4194 },
        'denver': { latitude: 39.7392, longitude: -104.9903 },
        'boston': { latitude: 42.3601, longitude: -71.0589 },
        'miami': { latitude: 25.7617, longitude: -80.1918 },
        'atlanta': { latitude: 33.7490, longitude: -84.3880 },
        // International Cities
        'london': { latitude: 51.5074, longitude: -0.1278 },
        'paris': { latitude: 48.8566, longitude: 2.3522 },
        'tokyo': { latitude: 35.6762, longitude: 139.6503 },
        'sydney': { latitude: -33.8688, longitude: 151.2093 },
        'melbourne': { latitude: -37.8136, longitude: 144.9631 },
        'toronto': { latitude: 43.6532, longitude: -79.3832 },
        'vancouver': { latitude: 49.2827, longitude: -123.1207 },
        'berlin': { latitude: 52.5200, longitude: 13.4050 },
        'madrid': { latitude: 40.4168, longitude: -3.7038 },
        'rome': { latitude: 41.9028, longitude: 12.4964 },
        'beijing': { latitude: 39.9042, longitude: 116.4074 },
        'shanghai': { latitude: 31.2304, longitude: 121.4737 },
        'mumbai': { latitude: 19.0760, longitude: 72.8777 },
        'singapore': { latitude: 1.3521, longitude: 103.8198 },
        'dubai': { latitude: 25.2048, longitude: 55.2708 },
      };
      
      const normalizedCity = city.toLowerCase().trim();
      
      if (cityCoordinates[normalizedCity]) {
        return cityCoordinates[normalizedCity];
      }
      
      for (const [key, coords] of Object.entries(cityCoordinates)) {
        if (normalizedCity.includes(key) || key.includes(normalizedCity)) {
          return coords;
        }
      }
      
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  };

  // Handle applying filters from FilterPanel
  const handleApplyFilters = useCallback(async (newFilters: ExtendedFilters) => {
    const filtersWithTimezone = {
      ...newFilters,
      timezone: selectedTimezone,
    };
    
    if (filtersWithTimezone.city && filtersWithTimezone.city.trim() !== '') {
      const geocoded = await geocodeCity(filtersWithTimezone.city, filtersWithTimezone.country);
      
      if (geocoded) {
        setManualLocation(geocoded);
        setLocationSource('manual');
      }
    } else {
      setManualLocation(null);
    }
    
    setCurrentFilters(filtersWithTimezone);
    setAppliedFilters(filtersWithTimezone);
  }, [selectedTimezone, appliedFilters]);

  if (authLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <MainContainer>
      <Header 
        user={user} 
        onSignOut={signOut}
        onLoginClick={() => setLoginDialogOpen(true)}
      />
      
      <WeekNavigation
        currentWeek={currentWeek}
        onPreviousWeek={handlePreviousWeek}
        onNextWeek={handleNextWeek}
        onJumpToDate={handleJumpToDate}
        selectedTimezone={selectedTimezone}
        onTimezoneChange={handleTimezoneChange}
      />
      
      {effectiveLocation && (
        <LocationBanner>
          <LocationContent>
            <LocationOnIcon sx={{ fontSize: '0.875rem', color: '#84b817' }} />
            <span>
              Using location: {
                locationSource === 'manual' 
                  ? `${appliedFilters.city}${appliedFilters.country ? `, ${appliedFilters.country}` : ''} (manual)`
                  : locationSource === 'browser'
                  ? 'Your current location (detected)'
                  : locationSource === 'inferred'
                  ? `${selectedTimezone} area (inferred from timezone)`
                  : 'Unknown'
              }
              {appliedFilters.radius && ` • ${appliedFilters.radius}mi radius`}
              {` • TZ: ${formatTimezoneForDisplay(selectedTimezone)}`}
            </span>
          </LocationContent>
        </LocationBanner>
      )}

      <MainContent>
        {/* Publish Schedule Banner */}
        {shouldShowBanner && !bannerLoading && (
          <PublishScheduleBanner 
            onPublishClick={handlePublishSchedule}
            onClose={onCloseBanner}
          />
        )}

        {/* Upcoming Matches Gallery */}
        {user && (
          <UpcomingMatchesGallery
            matches={upcomingMatches}
            loading={loadingMatches}
            currentUserId={user.uid}
            onAcceptMatch={handleAcceptMatch}
            onDeclineMatch={handleDeclineMatch}
            onManageSchedule={handleManageSchedule}
            onViewDetails={handleViewDetails}
          />
        )}

        {/* Available Players List - Now with server-side pagination */}
        <Box sx={{ 
          backgroundColor: '#ffffff', 
          borderRadius: '12px', // Increased border radius
          border: '1px solid #e5e5e5', // Added border
          p: 2.5, // Increased padding
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)', // Subtle shadow
        }}>
          <PlayerSlotsList
            slots={playerSlots}
            loading={loadingSlots}
            timeRange={null}
            onChallenge={handleChallenge}
            onSlotsUpdate={handleSlotsUpdate}
            displayTimezone={selectedTimezone}
            filters={appliedFilters}
            userLocation={effectiveLocation}
            hasMore={hasMoreSlots}
            onLoadMore={handleLoadMore}
            loadingMore={loadingMore}
            onFilterClick={() => setFilterDrawerOpen(true)}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </MainContent>
      
      <FilterPanel
        filters={currentFilters}
        onApplyFilters={handleApplyFilters}
        open={filterDrawerOpen}
        onOpenChange={setFilterDrawerOpen}
        hideFab={true}
      />
      
      <Footer />
      
      <LoginDialog
        open={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
      />
    </MainContainer>
  );
};

export default MainPage;